<template>
  <v-container>
    <v-card class="mt-5">
      <div class="text-right text-h5 font-weight-medium pa-3">
        Create Template
      </div>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="ml-12 mr-12 mt-8"
      >
        <v-row>
          <v-col cols="1" />
          <v-col cols="4">
            <v-text-field
              v-model="template.title"
              :counter="30"
              :rules="nameRules"
              label="Title"
              required
            ></v-text-field>

            <v-text-field
              v-model="template.perm"
              :counter="10"
              label="Perm"
              readonly
              disabled
              placeholder="RockBingo"
            ></v-text-field>

            <v-file-input
              label="Logo"
              v-model="logo"
              required
              clearable
            ></v-file-input>
          </v-col>
          <v-col cols="1" />
          <v-col cols="4">
            <v-select
              v-model="template.type"
              :items="gameTypes"
              disabled
              readonly
              label="Game Type"
              required
            ></v-select>

            <v-text-field
              v-model="template.pages"
              :rules="pageRules"
              type="number"
              min="1"
              max="12"
              oninput="validity.valid||(value='');"
              label="Number of Pages"
              :readonly="template.flyer"
              :disabled="template.flyer"
            ></v-text-field>

            <v-row>
              <v-col cols="6">
                <v-checkbox
                  v-model="template.allowDoubles"
                  label="Double Book"
                  required
                ></v-checkbox>
              </v-col>

              <v-col cols="6">
                <v-checkbox
                  v-model="template.flyer"
                  label="Flyer"
                  required
                  @change="updatePagesForFlyer(template.flyer)"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mt-10">
          <v-col cols="1" />
          <v-col cols="9">
            <v-data-table
              :headers="headers"
              :items="colourChoices"
              item-key="id"
              :show-select="false"
              :disable-pagination="true"
              :hide-default-footer="true"
              class="page__table"
            >
              <template v-slot:body="props">
                <draggable :list="props.items" tag="tbody" @end="endDrag">
                  <tr v-for="(item, index) in props.items" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <v-menu
                        v-model="colourPicker"
                        :close-on-content-click="true"
                        :nudge-width="200"
                        offset-x
                        :id="index"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-on="on"
                            class="square"
                            v-bind="attrs"
                            @click="showColours(index)"
                            :style="{ background: item.attributeValue }"
                        /></template>
                        <v-list>
                          <v-list-item
                            v-for="(colour, i) in getSystemColours"
                            :key="i"
                            @click="colourChange(colour, index)"
                          >
                            <template v-slot:default>
                              <v-list-item-action>
                                <div
                                  class="square"
                                  :style="{
                                    background: colour.attributeValue,
                                  }"
                                />
                              </v-list-item-action>

                              <v-list-item-content>
                                <v-list-item-title>{{
                                  colour.attributeName
                                }}</v-list-item-title>
                              </v-list-item-content>
                            </template>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </td>
                    <td>{{ item.attributeName }}</td>
                    <td><v-icon>mdi-reorder-horizontal</v-icon></td>
                  </tr>
                </draggable>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-row class="mt-16">
          <v-col class="text-right pt-3">
            <v-btn class="mr-4" @click="reset"> Clear </v-btn>
            <v-btn color="primary" class="mr-4" @click="submit"> Save </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import Draggable from "vuedraggable";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "TemplateDesigner",
  components: {
    Draggable,
  },
  created() {
    this.fetchColours();
  },
  computed: {
    ...mapGetters(["getSystemColours"]),
    colourChoices() {
      return this.template.flyer
        ? this.getSystemColours.slice(0, 1)
        : this.getSystemColours.slice(0, this.template.pages);
    },
  },

  data: () => ({
    valid: false,
    template: {
      title: "",
      perm: "RockBingo",
      pages: "",
      panels: "6",
      type: null,
      allowDoubles: false,
      flyer: false,
      gameColours: [],
    },
    colourPicker: false,
    updateIndex: null,
    logo: null,
    nameRules: [
      (v) => !!v || "Name is required",
      (v) => (v && v.length <= 30) || "Name must be less than 30 characters",
    ],
    pageRules: [
      (v) => !!v || "Number of pages is required, or else choose flyer",
    ],
    gameTypes: ["90 Number", "75 Number", "80 Number", "Other"],
    headers: [
      { text: "Order", sortable: false, width: "10%" },
      { text: "Colour", value: "hex", sortable: false, width: "10%" },
      { text: "Name", value: "name", sortable: false, width: "70%" },
      { text: "Reorder", sortable: false, width: "10%" },
    ],
  }),

  methods: {
    ...mapActions(["saveTemplate", "fetchColours"]),
    submit() {
      this.template.gameColours = this.colourChoices;
      this.saveTemplate({ template: this.template, logo: this.logo });
    },
    colourChange(colour) {
      const currentColourPosition = this.getSystemColours.indexOf(colour);
      const selectedColour = this.getSystemColours.splice(currentColourPosition, 1)[0];
      const swappedColour = this.getSystemColours.splice(this.updateIndex, 1)[0];
      this.getSystemColours.splice(this.updateIndex, 0, selectedColour);
      this.getSystemColours.splice(currentColourPosition, 0, swappedColour);
    },
    showColours(index){
      this.updateIndex = index;
    },
    validate() {
      this.$refs.form.validate();
      if (this.valid) {
        // TODO - Validation not working
        console.log("I just continued anyway");
      }
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    updatePagesForFlyer(isFlyer) {
      if (isFlyer) {
        this.template.pages = "1";
      }
    },
    endDrag(event) {
      const movedItem = this.getSystemColours.splice(event.oldIndex, 1)[0];
      this.getSystemColours.splice(event.newIndex, 0, movedItem);
    },
  },
};
</script>

<style scoped>
.square {
  width: 2vw;
  height: 2vw;
  min-width: 30px;
  min-height: 30px;
  border: rgb(73, 73, 73);
  border-style: solid;
  border-radius: 2px;
}
</style>


